import * as React from 'react'
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation'
import { usePhotoStack } from '@web/app/[locale]/clubhouse/[slug]/(social)/discussions/_components/photo-viewer/photo-stack-context'
import { useFeedRoute } from '../contexts/feed-route-context'

export function usePhotoNavigation({
  postSlug,
  commentId,
  firstAssetId,
}: {
  postSlug?: string | null
  commentId?: string
  firstAssetId?: string | number | null
}) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const router = useRouter()
  const { slug } = useParams()
  const stack = usePhotoStack()

  const currentAssetId = searchParams.get('assetid')
  const currentCid = searchParams.get('cid')

  const prevQuery = React.useMemo(() => {
    const sp = new URLSearchParams(searchParams.toString())
    sp.delete('prev')
    return sp.toString()
  }, [searchParams])

  const base = React.useMemo(() => {
    const root = (pathname || '/').replace(/\/photo\/?$/, '')
    return root.endsWith('/') ? root : `${root}/`
  }, [pathname])

  const photoHref =
    firstAssetId !== null && firstAssetId !== undefined && commentId
      ? `${base}photo?&postslug=${encodeURIComponent(postSlug ?? '')}&assetid=${encodeURIComponent(String(firstAssetId ?? 0))}&cid=${encodeURIComponent(
          String(commentId),
        )}${prevQuery ? `&prev=${encodeURIComponent(prevQuery)}` : ''}`
      : null

  const hideInlineImage =
    currentCid !== null && currentCid !== undefined && String(commentId) === currentCid

  const { basePath } = useFeedRoute()
  const redirectUrlBase = slug ? `${basePath}/${slug}/redirect?href=` : `${basePath}/redirect?href=`

  const openPhoto = React.useCallback(
    (e: React.MouseEvent) => {
      if (!photoHref) {
        return
      }
      // allow new tab/window
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
        return
      }
      e.preventDefault()

      // If we're already in the PhotoViewer, remember where we came from
      if (currentAssetId) {
        stack.push({ assetid: currentAssetId, cid: currentCid, postslug: postSlug })
      }

      router.push(photoHref, { scroll: false })
    },
    [photoHref, router, currentAssetId, currentCid, stack, postSlug],
  )

  return {
    prevQuery,
    photoHref,
    hideInlineImage,
    redirectUrlBase,
    openPhoto,
  }
}
